import { InjectionToken, Injectable, inject } from '@angular/core';
import { AiAssistantOwner } from '@cureskin/api-client-rn';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConnectionService } from '@services/connection-service';
import {
  AiAssistantChatState,
  AiAssistantErrorState,
  AiAssistantMessage,
  AiAssistantPaginationState,
} from './ai-assistant-chat.types';

export const AI_CHAT_POLL_INTERVAL_MS = new InjectionToken<number>('AI_CHAT_POLL_INTERVAL_MS', {
  factory: (): number => 1000,
});
export const AI_CHAT_RETRY_DELAY_MS = new InjectionToken<number>('AI_CHAT_RETRY_DELAY_MS', {
  factory: (): number => 1000,
});
export const AI_CHAT_START_DELAY_MS = new InjectionToken<number>('AI_CHAT_START_DELAY_MS', {
  factory: (): number => 5000,
});
export const AI_CHAT_STATUS_CHECK_INTERVAL_MS = new InjectionToken<number>('AI_CHAT_STATUS_CHECK_INTERVAL_MS', {
  factory: (): number => 60000,
});
export const AI_CHAT_MAX_WAIT_MS = new InjectionToken<number>('AI_CHAT_MAX_WAIT_MS', {
  factory: (): number => 5 * 60 * 1000,
});
export const AI_CHAT_STALE_THRESHOLD_MS = new InjectionToken<number>('AI_CHAT_STALE_THRESHOLD_MS', {
  factory: (): number => 48 * 60 * 60 * 1000,
});

@Injectable()
export class AiAssistantChatService {
  private static readonly DEFAULT_PAGE_SIZE = 20;

  private readonly connectionService = inject(ConnectionService);
  private readonly pollIntervalMs = inject(AI_CHAT_POLL_INTERVAL_MS);
  private readonly retryDelayMs = inject(AI_CHAT_RETRY_DELAY_MS);
  private readonly startDelayMs = inject(AI_CHAT_START_DELAY_MS);
  private readonly statusCheckIntervalMs = inject(AI_CHAT_STATUS_CHECK_INTERVAL_MS);
  private readonly maxWaitMs = inject(AI_CHAT_MAX_WAIT_MS);
  private readonly staleThresholdMs = inject(AI_CHAT_STALE_THRESHOLD_MS);

  private readonly state = new BehaviorSubject<AiAssistantChatState>({
    messages: [],
    isThinking: false,
    latestMessage: null,
    isLoadingOldMessages: false,
    errorState: 'none',
  });

  private readonly pagination: AiAssistantPaginationState = {
    hasMore: true,
    pageSize: AiAssistantChatService.DEFAULT_PAGE_SIZE,
  };

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private isSettled: boolean = false;
  private pollStartedAtMs: number | null = null;
  private lastStatusCheckAtMs: number | null = null;
  private awaitingReplyToMessageId: string | null = null;
  private newestKnownTimestamp: string | null = null;
  private readonly seenMessageIds = new Set<string>();

  get showThinking$(): Observable<boolean> {
    return this.state.pipe(map(state => state.isThinking));
  }

  get allChats$(): Observable<AiAssistantMessage[]> {
    return this.state.pipe(map(state => state.messages));
  }

  get latestMessage$(): Observable<AiAssistantMessage | null> {
    return this.state.pipe(map(state => state.latestMessage));
  }

  get loadingOldMessages$(): Observable<boolean> {
    return this.state.pipe(map(state => state.isLoadingOldMessages));
  }

  get errorState$(): Observable<AiAssistantErrorState> {
    return this.state.pipe(map(state => state.errorState));
  }

  get currentMessages(): AiAssistantMessage[] {
    return this.state.value.messages;
  }

  setThinking(value: boolean): void {
    this.updateState({ isThinking: value });
  }

  async loadChatHistory(): Promise<AiAssistantMessage[]> {
    this.pagination.hasMore = true;
    const fetched = await this.connectionService.listAiAssistantMessages(undefined, false, this.pagination.pageSize);
    this.pagination.hasMore = fetched.length >= this.pagination.pageSize;

    const messages = [...fetched].reverse();
    this.seenMessageIds.clear();
    messages.forEach(message => this.markSeen(message));
    this.newestKnownTimestamp = this.newestCreatedAt(messages) ?? this.newestKnownTimestamp;
    this.updateMessagesState(messages);
    this.applyErrorStateForLatest(messages[messages.length - 1] ?? null);
    return messages;
  }

  async loadMoreMessages(): Promise<boolean> {
    if (!this.pagination.hasMore || this.state.value.isLoadingOldMessages) {
      return false;
    }
    this.updateState({ isLoadingOldMessages: true });
    try {
      const oldestLoadedTimestamp = this.state.value.messages[0]?.createdAt;
      const fetched = await this.connectionService.listAiAssistantMessages(oldestLoadedTimestamp, false, this.pagination.pageSize);
      this.pagination.hasMore = fetched.length >= this.pagination.pageSize;

      const olderMessages = [...fetched].reverse().filter(message => this.markSeen(message));
      const merged = [...olderMessages, ...this.state.value.messages];
      this.updateState({
        messages: merged,
        latestMessage: merged.length ? merged[merged.length - 1] : null,
      });
      return this.pagination.hasMore;
    } finally {
      this.updateState({ isLoadingOldMessages: false });
    }
  }

  async sendMessage(text: string): Promise<AiAssistantMessage[]> {
    const message = text.trim();
    if (!message) return [];

    this.resetPolling();
    this.clearError();
    this.setThinking(true);
    const localMessage = this.createLocalUserMessage(message);
    this.addLocalMessages([localMessage]);

    try {
      const replies = await this.connectionService.sendAiAssistantMessage({ message });

      const userEcho = replies.find(reply => reply.owner === AiAssistantOwner.USER);
      const botReplies = replies.filter(reply => reply.owner !== AiAssistantOwner.USER);
      if (userEcho) this.replaceMessageById(localMessage.objectId, userEcho);
      this.newestKnownTimestamp = this.newestCreatedAt(botReplies) ?? userEcho?.createdAt ?? this.newestKnownTimestamp;

      if (botReplies.length) {
        this.mergeServerMessages(botReplies);
        this.setThinking(false);
      } else {
        this.startPollingForReply();
      }
      return replies;
    } catch (error) {
      this.setThinking(false);
      throw error;
    }
  }

  async retryLatestMessage(): Promise<void> {
    const messageId = this.awaitingReplyToMessageId ?? this.state.value.latestMessage?.objectId ?? null;
    if (!messageId || messageId.startsWith('local_')) return;

    this.resetPolling();
    this.clearError();
    this.setThinking(true);
    try {
      const replies = await this.connectionService.retryAiAssistantMessage(messageId);
      const userEcho = replies.find(reply => reply.owner === AiAssistantOwner.USER);
      const botReplies = replies.filter(reply => reply.owner !== AiAssistantOwner.USER);
      if (userEcho) this.replaceMessageById(userEcho.objectId, userEcho);
      this.newestKnownTimestamp = this.newestCreatedAt(botReplies) ?? this.newestKnownTimestamp;

      if (botReplies.length) {
        this.mergeServerMessages(botReplies);
        this.setThinking(false);
      } else {
        const anchor = userEcho ? this.toMs(userEcho.updatedAt) : this.nowMs();
        this.startPollingForReply(Number.isNaN(anchor) ? this.nowMs() : anchor);
      }
    } catch {
      this.setThinking(false);
      this.markRetryable();
    }
  }

  addLocalMessages(messages: AiAssistantMessage[]): void {
    if (!messages.length) return;
    const merged = [...this.state.value.messages, ...messages];
    this.updateState({ messages: merged, latestMessage: merged[merged.length - 1] });
  }

  replaceMessages(messages: AiAssistantMessage[]): void {
    this.updateMessagesState(messages);
  }

  resumePollingIfAwaitingReply(): void {
    if (this.state.value.errorState !== 'none') return;
    if (this.state.value.latestMessage?.owner === AiAssistantOwner.USER) {
      this.startPollingForReply();
    }
  }

  stopBotPolling(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  isMessageErrored(message: AiAssistantMessage, nextMessage?: AiAssistantMessage): boolean {
    if (message.owner !== AiAssistantOwner.USER) return false;
    if (this.isServerFlaggedFailure(message)) return true;
    if (nextMessage) {
      const replyDelayMs = this.toMs(nextMessage.createdAt) - this.toMs(message.updatedAt);
      if (!Number.isNaN(replyDelayMs) && replyDelayMs > this.maxWaitMs) return true;
    }
    return false;
  }

  private startPollingForReply(anchorMs?: number): void {
    const latest = this.state.value.latestMessage;
    if (latest?.owner !== AiAssistantOwner.USER) return;
    const messageId = latest.objectId;
    if (!messageId || messageId.startsWith('local_')) return;

    this.resetPolling();
    this.clearError();
    this.setThinking(true);
    this.awaitingReplyToMessageId = messageId;
    const resolvedAnchor = anchorMs ?? this.toMs(latest.updatedAt);
    this.pollStartedAtMs = Number.isNaN(resolvedAnchor) ? this.nowMs() : resolvedAnchor;
    this.lastStatusCheckAtMs = this.pollStartedAtMs;

    if (this.hasExceededMaxWait()) {
      this.markRetryable();
      return;
    }
    const startDelayMs = Math.max(0, this.pollStartedAtMs + this.startDelayMs - this.nowMs());
    this.scheduleNextCycle(startDelayMs);
  }

  private resetPolling(): void {
    this.stopBotPolling();
    this.isSettled = false;
  }

  private scheduleNextCycle(delayMs: number): void {
    this.pollTimer = setTimeout((): void => { void this.runPollCycle(); }, delayMs);
  }

  private async runPollCycle(): Promise<void> {
    if (this.isSettled) return;
    if (this.hasExceededMaxWait()) {
      this.markRetryable();
      return;
    }

    const requestSucceeded = await this.fetchAndMergeBotReply();
    if (this.isSettled) return;

    await this.runStatusCheckIfDue();
    if (this.isSettled) return;

    this.scheduleNextCycle(requestSucceeded ? this.pollIntervalMs : this.retryDelayMs);
  }

  private async fetchAndMergeBotReply(): Promise<boolean> {
    try {
      const fetched = await this.connectionService.listAiAssistantMessages(
        this.newestKnownTimestamp ?? undefined, true, this.pagination.pageSize);
      if (this.isSettled) return true;
      const added = this.mergeServerMessages(fetched.filter(message => message.owner !== AiAssistantOwner.USER));
      if (added.length) this.markSucceeded();
      return true;
    } catch {
      return false;
    }
  }

  private async runStatusCheckIfDue(): Promise<void> {
    if (this.isSettled || !this.awaitingReplyToMessageId) return;
    if (this.nowMs() - (this.lastStatusCheckAtMs ?? this.nowMs()) < this.statusCheckIntervalMs) return;

    this.lastStatusCheckAtMs = this.nowMs();
    try {
      const message = await this.connectionService.getAiAssistantMessage(this.awaitingReplyToMessageId);
      if (this.isSettled || !message) return;
      this.replaceMessageById(message.objectId, message);
      if (this.isServerFlaggedFailure(message)) this.markRetryable();
    } catch {
      // A failed status check is transient — retry on the next cycle.
    }
  }

  private applyErrorStateForLatest(latest: AiAssistantMessage | null): void {
    if (!latest) {
      this.updateState({ errorState: 'none' });
      return;
    }
    if (this.isStale(latest)) {
      this.updateState({ errorState: 'stale', isThinking: false });
      return;
    }
    if (latest.owner === AiAssistantOwner.USER && this.isFailedOrTimedOut(latest)) {
      this.updateState({ errorState: 'retryable', isThinking: false });
      return;
    }
    this.updateState({ errorState: 'none' });
  }

  private isFailedOrTimedOut(message: AiAssistantMessage): boolean {
    return this.isServerFlaggedFailure(message) || this.nowMs() - this.toMs(message.updatedAt) > this.maxWaitMs;
  }

  private markSucceeded(): void {
    this.isSettled = true;
    this.stopBotPolling();
    this.awaitingReplyToMessageId = null;
    this.setThinking(false);
    this.clearError();
  }

  private markRetryable(): void {
    this.isSettled = true;
    this.stopBotPolling();
    this.updateState({ isThinking: false, errorState: 'retryable' });
  }

  private clearError(): void {
    if (this.state.value.errorState !== 'none') this.updateState({ errorState: 'none' });
  }

  private hasExceededMaxWait(): boolean {
    return this.pollStartedAtMs !== null && this.nowMs() - this.pollStartedAtMs > this.maxWaitMs;
  }

  private isStale(message: AiAssistantMessage): boolean {
    const updatedMs = this.toMs(message.updatedAt);
    return !Number.isNaN(updatedMs) && this.nowMs() - updatedMs > this.staleThresholdMs;
  }

  private isServerFlaggedFailure(message: AiAssistantMessage): boolean {
    return !!message.requestId && !!message.objectId && message.requestId === message.objectId;
  }

  private mergeServerMessages(messages: AiAssistantMessage[]): AiAssistantMessage[] {
    const unseenMessages = messages.filter(message => this.markSeen(message));
    if (!unseenMessages.length) return [];
    const merged = [...this.state.value.messages, ...unseenMessages];
    this.newestKnownTimestamp = this.newestCreatedAt(unseenMessages) ?? this.newestKnownTimestamp;
    this.updateState({ messages: merged, latestMessage: merged[merged.length - 1] });
    return unseenMessages;
  }

  private replaceMessageById(targetId: string | undefined, replacement: AiAssistantMessage): void {
    if (!targetId) return;
    let replaced = false;
    const messages = this.state.value.messages.map(message => {
      if (message.objectId === targetId) {
        replaced = true;
        return { ...message, ...replacement };
      }
      return message;
    });
    if (!replaced) return;
    this.markSeen(replacement);
    this.updateState({ messages, latestMessage: messages.length ? messages[messages.length - 1] : null });
  }

  private markSeen(message: AiAssistantMessage): boolean {
    const id = message.objectId;
    if (!id) return true;
    if (this.seenMessageIds.has(id)) return false;
    this.seenMessageIds.add(id);
    return true;
  }

  private newestCreatedAt(messages: AiAssistantMessage[]): string | null {
    return messages.reduce<string | null>((newest, message) =>
      (message.createdAt && (!newest || message.createdAt > newest) ? message.createdAt : newest), null);
  }

  private nowMs(): number {
    return Date.now();
  }

  private toMs(timestamp?: string): number {
    return timestamp ? Date.parse(timestamp) : Number.NaN;
  }

  private createLocalUserMessage(content: string): AiAssistantMessage {
    return {
      objectId: `local_${crypto.randomUUID()}`,
      owner: AiAssistantOwner.USER,
      content,
      createdAt: new Date().toISOString(),
    };
  }

  private updateMessagesState(messages: AiAssistantMessage[]): void {
    this.updateState({
      messages,
      latestMessage: messages.length ? messages[messages.length - 1] : null,
    });
  }

  private updateState(partial: Partial<AiAssistantChatState>): void {
    this.state.next({ ...this.state.value, ...partial });
  }
}
